import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import { PermissionGuard } from "~/components/permission-guard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { employeeLinksOverviewQuery } from "~/modules/employee/queries";
import { internalAccountsQuery } from "~/modules/lookup/queries";

import { AllAccountsTab } from "./-components/all-accounts-tab";
import { EmployeesTab } from "./-components/employees-tab";

export const Route = createFileRoute("/_dash/users/internal-accounts")({
  validateSearch: z.object({
    tab: z.enum(["employees", "accounts"]).optional().default("employees").catch("employees"),
  }),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(internalAccountsQuery.queryOptions()),
      context.queryClient.ensureQueryData(employeeLinksOverviewQuery.queryOptions()),
    ]),
  component: RouteComponent,
});

function RouteComponent() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <PermissionGuard permission="internal-account:write">
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-bold">Internal Accounts</h1>
          <p className="text-sm text-muted-foreground">
            Employee roster, account linkage, and flagged internal accounts
          </p>
        </div>

        <Tabs
          value={tab}
          onValueChange={(value) =>
            navigate({
              search: { tab: value === "accounts" ? "accounts" : "employees" },
              replace: true,
            })
          }
        >
          <TabsList>
            <TabsTrigger value="employees">Employees</TabsTrigger>
            <TabsTrigger value="accounts">All Accounts</TabsTrigger>
          </TabsList>
          <TabsContent value="employees" className="mt-4">
            <EmployeesTab />
          </TabsContent>
          <TabsContent value="accounts" className="mt-4">
            <AllAccountsTab />
          </TabsContent>
        </Tabs>
      </div>
    </PermissionGuard>
  );
}
